import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Shield, FileText, Database } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TicketAcceptance() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedGDPR, setAcceptedGDPR] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkAcceptance();
  }, [ticketId]);

  const checkAcceptance = async () => {
    try {
      // Check if already accepted
      const acceptanceRes = await axios.get(`${API_URL}/tickets/${ticketId}/check-acceptance`);
      
      if (acceptanceRes.data.accepted) {
        // Already accepted, redirect to download
        toast.success('Termini già accettati!');
        setTimeout(() => {
          // TODO: redirect to PDF download
          navigate('/');
        }, 1000);
        return;
      }

      // Get ticket info
      const ticketRes = await axios.get(`${API_URL}/tickets/${ticketId}/history`);
      setTicket(ticketRes.data.ticket);
      setLoading(false);
    } catch (error) {
      toast.error('Errore nel caricamento biglietto');
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!acceptedPrivacy || !acceptedTerms || !acceptedGDPR) {
      toast.error('Devi accettare tutti i termini per procedere');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/tickets/${ticketId}/accept-terms`, {
        privacy: acceptedPrivacy,
        terms: acceptedTerms,
        gdpr: acceptedGDPR,
        ip_address: 'client' // TODO: get real IP
      });

      toast.success('Termini accettati! Scaricando biglietto...');
      
      // TODO: Trigger PDF download
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error('Errore durante l\'accettazione');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-lg text-gray-600">Caricamento...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-center">
              Accettazione Termini e Condizioni
            </CardTitle>
            <p className="text-center text-gray-600 mt-2">
              Prima di scaricare il tuo biglietto, devi accettare i seguenti termini
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {ticket && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Biglietto per:</strong> {ticket.holder_name}
                </p>
                <p className="text-sm text-blue-900">
                  <strong>Email:</strong> {ticket.holder_email}
                </p>
              </div>
            )}

            {/* Privacy Policy */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Privacy Policy</h3>
                  <p className="text-sm text-gray-600">
                    Acconsento al trattamento dei miei dati personali secondo la Privacy Policy di ZonePass.
                    I miei dati saranno utilizzati esclusivamente per la gestione del biglietto e dell'evento.
                  </p>
                  <a href="/privacy-policy" target="_blank" className="text-blue-600 text-sm hover:underline">
                    Leggi Privacy Policy completa →
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-3">
                <Checkbox
                  id="privacy"
                  checked={acceptedPrivacy}
                  onCheckedChange={setAcceptedPrivacy}
                />
                <label htmlFor="privacy" className="text-sm font-medium cursor-pointer">
                  Accetto la Privacy Policy
                </label>
              </div>
            </div>

            {/* Terms of Service */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <FileText className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">Termini di Servizio</h3>
                  <p className="text-sm text-gray-600">
                    Accetto i Termini di Servizio di ZonePass. Il biglietto è personale e non trasferibile.
                    Devo presentare il QR code all'ingresso dell'evento.
                  </p>
                  <a href="/terms-of-service" target="_blank" className="text-blue-600 text-sm hover:underline">
                    Leggi Termini di Servizio completi →
                  </a>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-3">
                <Checkbox
                  id="terms"
                  checked={acceptedTerms}
                  onCheckedChange={setAcceptedTerms}
                />
                <label htmlFor="terms" className="text-sm font-medium cursor-pointer">
                  Accetto i Termini di Servizio
                </label>
              </div>
            </div>

            {/* GDPR */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <Database className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-2">GDPR</h3>
                  <p className="text-sm text-gray-600">
                    Dichiaro di aver preso visione dell'informativa sul trattamento dei dati personali
                    ai sensi del Regolamento UE 2016/679 (GDPR).
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2 mt-3">
                <Checkbox
                  id="gdpr"
                  checked={acceptedGDPR}
                  onCheckedChange={setAcceptedGDPR}
                />
                <label htmlFor="gdpr" className="text-sm font-medium cursor-pointer">
                  Accetto il trattamento dati secondo GDPR
                </label>
              </div>
            </div>

            <Button
              onClick={handleAccept}
              disabled={!acceptedPrivacy || !acceptedTerms || !acceptedGDPR || submitting}
              className="w-full"
              size="lg"
            >
              {submitting ? 'Elaborazione...' : 'Accetta e Scarica Biglietto'}
            </Button>

            <p className="text-xs text-center text-gray-500">
              Cliccando su "Accetta" confermi di aver letto e compreso tutti i termini sopra elencati
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
